import { normalizeRteImagesIn } from './rte-content-images.js'

/**
 * Fold accents and ligatures into an ASCII slug.
 *
 * @param {string} text
 * @returns {string}
 */
function foldHeadingText(text) {
  return text
    .replace(/œ/gi, 'oe')
    .replace(/æ/gi, 'ae')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * @param {string} text
 * @param {number} index
 * @returns {string}
 */
export function headingIdFromText(text, index = 0) {
  return foldHeadingText(text) || `section-${index + 1}`
}

/**
 * Pre-fold slug used by older TOC hashes (`résoudre` → `rsoudre`).
 * Kept so cold-load sitelinks and bookmarks still resolve.
 *
 * @param {string} text
 * @param {number} index
 * @returns {string}
 */
export function legacyHeadingIdFromText(text, index = 0) {
  const slug = text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')

  return slug || `section-${index + 1}`
}

/**
 * @param {ParentNode} mainContent
 * @param {string} hash
 * @returns {HTMLElement | null}
 */
function findHeadingBySlugHash(mainContent, hash) {
  const headings = mainContent.querySelectorAll('h2')

  for (const heading of headings) {
    if (!(heading instanceof HTMLElement)) continue

    const text = heading.textContent || ''

    if (
      headingIdFromText(text) === hash ||
      legacyHeadingIdFromText(text) === hash
    ) {
      return heading
    }
  }

  return null
}

/**
 * Assign a heading id. Merchant-authored ids are kept; generated slugs stay
 * on the first occurrence and later collisions get a numeric suffix (`-2`).
 *
 * @param {HTMLElement} heading
 * @param {number} [index]
 * @returns {string}
 */
export function assignHeadingId(heading, index = 0) {
  if (heading.id) return heading.id

  const baseId = headingIdFromText(heading.textContent || '', index)
  let candidateId = baseId
  let suffix = 2

  while (true) {
    const existing = document.getElementById(candidateId)
    if (!existing || existing === heading) break
    candidateId = `${baseId}-${suffix}`
    suffix += 1
  }

  heading.id = candidateId
  return candidateId
}

/**
 * Update the active TOC link without rescanning the full list when possible.
 *
 * @param {Element | null} previousActive
 * @param {Element | null} nextActive
 * @returns {Element | null}
 */
export function updateActiveTocLink(previousActive, nextActive) {
  if (previousActive === nextActive) return previousActive

  previousActive?.classList.remove('is-active')
  nextActive?.classList.add('is-active')
  return nextActive
}

/**
 * @typedef {{ updateHistory?: boolean, behavior?: ScrollBehavior }} ScrollToContentHeadingOptions
 */

/**
 * Scroll a heading into view using CSS scroll-margin (no sync layout math).
 *
 * @param {HTMLElement} target
 * @param {string} targetId
 * @param {ScrollToContentHeadingOptions} [options]
 */
export function scrollToContentHeading(target, targetId, options = {}) {
  const updateHistory = options.updateHistory !== false
  const behavior = options.behavior || 'smooth'

  target.scrollIntoView({ behavior, block: 'start' })

  if (
    updateHistory &&
    targetId &&
    typeof history !== 'undefined' &&
    history.pushState
  ) {
    history.pushState(null, '', `#${targetId}`)
  }
}

/**
 * Honor an existing `#heading-id` after JS assigns heading ids. Does not
 * write history — the fragment is already in the address bar.
 *
 * @param {ParentNode} [root]
 * @returns {boolean}
 */
export function restoreContentHeadingFromHash(root = document) {
  const hash = window.location.hash.replace(/^#/, '')
  if (!hash) return false

  const mainContent = root.querySelector('.content-sidebar-main')

  if (!mainContent) return false

  const lookupDocument = root instanceof Document ? root : document
  let target = lookupDocument.getElementById(hash)

  if (!(target instanceof HTMLElement) || !mainContent.contains(target)) {
    target = findHeadingBySlugHash(mainContent, hash)
  }

  if (!(target instanceof HTMLElement) || !mainContent.contains(target)) {
    return false
  }

  scrollToContentHeading(target, hash, {
    updateHistory: false,
    behavior: 'auto'
  })
  return true
}

/**
 * Zero-padded sommaire index (`0` → `01`).
 *
 * @param {number} index
 * @returns {string}
 */
export function formatArticleTocIndex(index) {
  return String(index + 1).padStart(2, '0')
}

/**
 * Replace the `__COUNT__` token in a translated sommaire meta line.
 *
 * @param {string} template
 * @param {number} count
 * @returns {string}
 */
export function formatArticleTocMeta(template, count) {
  return String(template).replaceAll('__COUNT__', String(count))
}

/**
 * True when a top-level article paragraph opens with "Bon à savoir" or "Astuce".
 *
 * @param {Element} element
 * @returns {boolean}
 */
export function isBonASavoirParagraph(element) {
  if (!(element instanceof HTMLParagraphElement)) return false
  if (element.closest('li')) return false

  const parent = element.parentElement

  if (!parent) return false

  const topLevel =
    parent.classList.contains('editorial-prose') ||
    parent.classList.contains('blog-post-content') ||
    parent.classList.contains('metafield-rich_text_field') ||
    parent.classList.contains('code-defaut-entry__prose') ||
    parent.tagName === 'RTE-FORMATTER'

  if (!topLevel) return false

  const text = (element.textContent || '')
    .replace(/\u00a0/g, ' ')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .trim()
    .toLowerCase()

  return /^(?:bon a savoir|astuce)\b/.test(text)
}

const SVG_NS = 'http://www.w3.org/2000/svg'

/**
 * Static info glyph for “Bon à savoir” callouts.
 *
 * @returns {SVGSVGElement}
 */
function createArticleCalloutIcon() {
  const svg = document.createElementNS(SVG_NS, 'svg')
  svg.setAttribute('width', '22')
  svg.setAttribute('height', '22')
  svg.setAttribute('viewBox', '0 0 24 24')
  svg.setAttribute('fill', 'none')
  svg.setAttribute('stroke', 'currentColor')
  svg.setAttribute('stroke-width', '2')
  svg.setAttribute('stroke-linecap', 'round')
  svg.setAttribute('stroke-linejoin', 'round')
  svg.setAttribute('aria-hidden', 'true')
  svg.setAttribute('focusable', 'false')

  const circle = document.createElementNS(SVG_NS, 'circle')
  circle.setAttribute('cx', '12')
  circle.setAttribute('cy', '12')
  circle.setAttribute('r', '9')

  const stem = document.createElementNS(SVG_NS, 'path')
  stem.setAttribute('d', 'M12 11v5')

  const dot = document.createElementNS(SVG_NS, 'path')
  dot.setAttribute('d', 'M12 8h.01')

  svg.append(circle, stem, dot)
  return svg
}

/**
 * Mark article paragraphs that should render as the amber callout.
 *
 * @param {ParentNode} root
 */
export function markArticleCallouts(root) {
  const scope =
    root.querySelector('.editorial-prose, .blog-post-content') || root
  const paragraphs = scope.querySelectorAll('p')

  for (const paragraph of paragraphs) {
    if (!isBonASavoirParagraph(paragraph)) continue

    paragraph.classList.add('editorial-callout')

    if (paragraph.querySelector('.editorial-callout__icon')) continue

    const icon = document.createElement('span')
    icon.className = 'editorial-callout__icon'
    icon.setAttribute('aria-hidden', 'true')
    icon.append(createArticleCalloutIcon())
    paragraph.prepend(icon)
  }
}

/**
 * @param {HTMLElement} section
 * @param {number} headingCount
 */
function bindArticleToc(section, headingCount) {
  const meta = section.querySelector('[data-article-toc-meta]')
  const template = meta?.getAttribute('data-article-toc-meta-template')

  if (meta && template) {
    meta.textContent = formatArticleTocMeta(template, headingCount)
  }

  const toc = section.querySelector('.editorial-toc')

  if (toc instanceof HTMLElement && headingCount === 0) {
    toc.hidden = true
  }

  const toggle = section.querySelector('[data-article-toc-toggle]')
  const panel = section.querySelector('[data-article-toc-panel]')

  if (
    !(toggle instanceof HTMLElement) ||
    !(panel instanceof HTMLElement) ||
    toggle.dataset.articleTocToggleBound === 'true'
  ) {
    return
  }

  toggle.dataset.articleTocToggleBound = 'true'
  toggle.addEventListener('click', () => {
    const expanded = toggle.getAttribute('aria-expanded') !== 'false'
    toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true')
    panel.hidden = expanded
  })
}

/**
 * @param {HTMLElement} list
 * @param {HTMLElement} heading
 * @param {number} index
 * @param {boolean} articleToc
 */
function appendTocLink(list, heading, index, articleToc) {
  const li = document.createElement('li')
  const anchor = document.createElement('a')
  const label = heading.textContent || ''

  anchor.href = `#${heading.id}`
  anchor.className = 'content-sidebar-link'

  if (articleToc) {
    const indexEl = document.createElement('span')
    indexEl.className = 'editorial-toc__index'
    indexEl.textContent = formatArticleTocIndex(index)

    const labelEl = document.createElement('span')
    labelEl.className = 'editorial-toc__label'
    labelEl.textContent = label

    anchor.append(indexEl, labelEl)
  } else {
    anchor.textContent = label
  }

  li.appendChild(anchor)
  list.appendChild(li)
}

/**
 * Build the title_nav TOC once and wire heading observers + smooth scroll.
 * Idempotent: skips link injection when the list already has children.
 */
export function initContentSidebar() {
  const sectionContainer = document.querySelector('.content-sidebar-section')
  const mainContent = document.querySelector('.content-sidebar-main')
  const sidebarList = document.querySelector('.content-sidebar-list')

  if (!sectionContainer) return

  const template = sectionContainer.getAttribute('data-template') || 'title_nav'

  if (mainContent) {
    mainContent
      .querySelectorAll('.rte, .metafield-rich_text_field')
      .forEach((root) => {
        normalizeRteImagesIn(root)
      })
  }

  if (mainContent && sidebarList && template === 'title_nav') {
    const articleToc = sectionContainer.hasAttribute('data-article-toc')
    const headings = mainContent.querySelectorAll('h2')
    const tocAlreadyBuilt = sidebarList.children.length > 0

    if (articleToc) {
      markArticleCallouts(mainContent)
    }

    headings.forEach((heading, index) => {
      if (!(heading instanceof HTMLElement)) return
      assignHeadingId(heading, index)
    })

    if (!tocAlreadyBuilt) {
      headings.forEach((heading, index) => {
        if (!(heading instanceof HTMLElement)) return
        if (!(sidebarList instanceof HTMLElement)) return
        appendTocLink(sidebarList, heading, index, articleToc)
      })
    }

    if (articleToc) {
      bindArticleToc(sectionContainer, headings.length)
    }

    if (sectionContainer.dataset.contentSidebarTocBound === 'true') {
      return
    }
    sectionContainer.dataset.contentSidebarTocBound = 'true'

    const tocLinks = sidebarList.querySelectorAll('.content-sidebar-link')
    /** @type {Element | null} */
    let activeLink = null
    /** @type {string | null} */
    let pendingHeadingId = null
    let activeFlushScheduled = false

    if (articleToc && tocLinks.length > 0) {
      activeLink = updateActiveTocLink(activeLink, tocLinks[0])
    }

    const flushActiveLink = () => {
      activeFlushScheduled = false
      if (!pendingHeadingId) return

      const nextActive =
        sidebarList.querySelector(
          `.content-sidebar-link[href="#${CSS.escape(pendingHeadingId)}"]`
        ) || null

      activeLink = updateActiveTocLink(activeLink, nextActive)
    }

    const observerOptions = {
      root: null,
      rootMargin: '-10% 0px -80% 0px',
      threshold: 0
    }

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue
        pendingHeadingId = entry.target.id
        if (!activeFlushScheduled) {
          activeFlushScheduled = true
          requestAnimationFrame(flushActiveLink)
        }
      }
    }, observerOptions)

    headings.forEach((heading) => observer.observe(heading))

    if (articleToc && headings.length > 0 && tocLinks.length > 0) {
      let scrollFrame = 0

      const syncActiveFromScroll = () => {
        scrollFrame = 0
        const threshold = (window.innerHeight || 800) * 0.25
        let currentId = headings[0]?.id || ''

        for (const heading of headings) {
          if (!(heading instanceof HTMLElement)) continue
          if (heading.getBoundingClientRect().top <= threshold) {
            currentId = heading.id
          } else {
            break
          }
        }

        if (!currentId) return

        const nextActive =
          sidebarList.querySelector(
            `.content-sidebar-link[href="#${CSS.escape(currentId)}"]`
          ) || null

        if (nextActive) {
          activeLink = updateActiveTocLink(activeLink, nextActive)
        }
      }

      window.addEventListener(
        'scroll',
        () => {
          if (scrollFrame) return
          scrollFrame = window.requestAnimationFrame(syncActiveFromScroll)
        },
        { passive: true }
      )
    }

    tocLinks.forEach((anchor) => {
      anchor.addEventListener('click', function (e) {
        const href = this.getAttribute('href') || ''
        const targetId = href.startsWith('#') ? href.slice(1) : ''
        const targetElement = targetId
          ? document.getElementById(targetId)
          : null
        if (!targetElement) return

        e.preventDefault()
        activeLink = updateActiveTocLink(activeLink, this)
        scrollToContentHeading(targetElement, targetId)
      })
    })

    restoreContentHeadingFromHash()
  }
}

// Build the table of contents as early as possible. Running synchronously when
// the DOM is already parsed (this module is deferred, so it can execute after
// DOMContentLoaded has fired) avoids both a missed build and a late layout
// shift from injecting the TOC links after first paint.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initContentSidebar)
} else {
  initContentSidebar()
}
